import { localizedAuthError } from "./http-ui.ts";

export const AUTH_PAGE_PATHS = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/bind-email",
  "/select-account",
  "/consent",
  "/change-password",
  "/account",
] as const;

export type AuthPage = (typeof AUTH_PAGE_PATHS)[number] extends `/${infer Page}`
  ? Page
  : never;

const authPages = new Set<AuthPage>(
  AUTH_PAGE_PATHS.map((path) => path.slice(1) as AuthPage),
);

export function resolveAuthPage(pathname: string): AuthPage {
  const page = pathname.replace(/^\/+|\/+$/g, "");
  return authPages.has(page as AuthPage) ? (page as AuthPage) : "login";
}

export function recoveryErrorMessage(code: string) {
  return localizedAuthError(code, "PASSWORD_RECOVERY_FAILED");
}

export function oauthQueryFromLocation(search: string) {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  if (!raw) return "";
  const params = new URLSearchParams(raw);
  const nested = params.get("oauth_query");
  if (nested) {
    const nestedParams = new URLSearchParams(nested);
    if (
      !nestedParams.has("client_id") ||
      !nestedParams.has("sig") ||
      !nestedParams.has("exp")
    ) return "";
    return nested;
  }
  if (!params.has("client_id") || !params.has("sig") || !params.has("exp")) return "";
  return raw;
}

export function maskEmail(email: string) {
  const [local = "", domain = ""] = email.split("@");
  const visible = local.length <= 2 ? local.slice(0, 1) : `${local.slice(0, 2)}${local.slice(-2)}`;
  const masked = local.length <= 2 ? `${visible}***` : `${visible.slice(0, 2)}***${visible.slice(-2)}`;
  return `${masked}@${domain}`;
}

export function bindEmailDestination(
  next: string,
  oauthQuery: string | null,
  email?: string,
) {
  if (next === "sign-in-with-email" && email) {
    const query = new URLSearchParams();
    if (oauthQuery) query.set("oauth_query", oauthQuery);
    query.set("email", email);
    query.set("notice", "email-bound");
    return `/login?${query.toString()}`;
  }
  if (next !== "authenticated") return "/login";
  return oauthQuery
    ? `/select-account?oauth_query=${encodeURIComponent(oauthQuery)}`
    : "/account";
}

export function loginAttemptOrder(identifier: string) {
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
  return validEmail
    ? (["legacy", "email"] as Array<"legacy" | "email">)
    : (["legacy"] as Array<"legacy" | "email">);
}
